import { FC, useEffect } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { InspectionLocationMap } from '@/components/maps/InspectionLocationMap';
import { LocationCaptureMetaBar } from '@/features/inspection-list/components/LocationCaptureMetaBar';
import styles from './VerificationMapExpandOverlay.module.scss';

type VerificationMapExpandOverlayProps = {
  current: { lat: number; lng: number } | null;
  currentAccuracyMetres: number | null;
  capturedAt: string | null;
  onClose: () => void;
};

export const VerificationMapExpandOverlay: FC<VerificationMapExpandOverlayProps> = ({
  current,
  currentAccuracyMetres,
  capturedAt,
  onClose,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('factory_verification_expand_map')}
      onClick={onClose}
    >
      <div className={styles.panel} onClick={(event) => event.stopPropagation()}>
        <div className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>{t('factory_verification_expand_map')}</h2>
          <button
            type="button"
            className={styles.closeButton}
            aria-label={t('close')}
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div className={styles.mapFrame}>
          <InspectionLocationMap
            className={styles.expandedMap}
            registered={null}
            current={current}
            currentAccuracyMetres={currentAccuracyMetres}
          />
        </div>
        {capturedAt && (
          <LocationCaptureMetaBar capturedAt={capturedAt} accuracyMetres={currentAccuracyMetres} />
        )}
      </div>
    </div>,
    document.body
  );
};
